import React, { FC, useCallback, useMemo, useRef } from 'react';

import { MenuObjectTypes } from 'enums/menu';
import { useProductWizardContext } from 'state/product-wizard-v2';
import useProductWizardFlow from 'state/product-wizard-v2/hooks/use-product-wizard-flow';
import { isCombo } from 'utils/menu';

import { IModifierSelections } from '../modifier/types';
import { DEFAULT_SLOT_KEY } from '../product-detail-flow/product-item-flow/constants';

import { ModifierGroup } from './product-customization-modifier-group';
import { ModifierGroupContainer } from './styled';
import { IModifierGroup, IProductCustomizationModifiersProps } from './types';
import { buildModifierGroups, stabilizeModifierGroupSelectionsLegacy } from './utils';

export const ProductCustomizationModifiers: FC<IProductCustomizationModifiersProps> = ({
  modifierSelections,
  onModifierSelectionsChange,
  pickerAspectExclusions,
  selectedItem,
  hasMultipleComboSlotOptions = false,
}) => {
  const { options: selectedItemModifiers } = selectedItem;
  const { menuObject, updateSelectionStateWithId } = useProductWizardContext();
  const { slotKeyToCustomize } = useProductWizardFlow();
  const selectionsCacheRef = useRef(new Map<string, IModifierSelections>());

  const handleModifierSelectionChange = useCallback(
    (
      selections: IModifierSelections,
      modifier: Parameters<typeof onModifierSelectionsChange>[1]
    ) => {
      onModifierSelectionsChange(selections, modifier);
      updateSelectionStateWithId({
        id: slotKeyToCustomize || DEFAULT_SLOT_KEY,
        isDirty: true,
        type: isCombo(menuObject) ? MenuObjectTypes.COMBO_SLOT : MenuObjectTypes.ITEM,
      });
    },
    [menuObject, onModifierSelectionsChange, slotKeyToCustomize, updateSelectionStateWithId]
  );

  const modifierGroupsProps = useMemo<IModifierGroup[]>(() => {
    if (!selectedItemModifiers?.length) {
      return [];
    }

    const groups = buildModifierGroups(
      selectedItemModifiers,
      modifierSelections,
      pickerAspectExclusions
    );

    return stabilizeModifierGroupSelectionsLegacy(groups, selectionsCacheRef.current);
  }, [selectedItemModifiers, modifierSelections, pickerAspectExclusions]);

  return (
    <ModifierGroupContainer testID="product-customization-modifiers">
      {modifierGroupsProps.map(modifierGroupProps => (
        <ModifierGroup
          {...modifierGroupProps}
          key={modifierGroupProps.key ?? modifierGroupProps.displayGroupName}
          isFullContainer={hasMultipleComboSlotOptions}
          onModifierSelectionChange={handleModifierSelectionChange}
          selectedItem={selectedItem}
        />
      ))}
    </ModifierGroupContainer>
  );
};
